import { Parameter } from "../../common/types/parameters";
import type { SupplementalHeatingTooltipBreakdown } from "./barchart";

export const SUPPLEMENTAL_DISTRICT_HEATING_LCC_COST_SUFFIXES = [
  "InvestmentLCCost",
  "MaintenanceLCCost",
  "EnergyLCCost",
  "LoadLCCost",
  "FlowLCCost",
  "ReinvestmentLCCost",
] as const;

export function escapeTooltipHtml(raw: string): string {
  return raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");
}

export function supplementalDistrictHeatingParameterId(
  heatingTechnologyId: string,
  suffix: string
): string | undefined {
  switch (heatingTechnologyId) {
    case "AirSource":
      return `AirSourceSupplementalDistrictHeating${suffix}`;
    case "GroundSource":
      return `GroundSourceSupplementalDistrictHeating${suffix}`;
    case "ExhaustAir":
      return `ExhaustAirSupplementalDistrictHeating${suffix}`;
    default:
      return undefined;
  }
}

export function canHaveSupplementalDistrictHeating(
  heatingTechnologyId: string
): boolean {
  return (
    heatingTechnologyId === "AirSource" ||
    heatingTechnologyId === "GroundSource" ||
    heatingTechnologyId === "ExhaustAir"
  );
}

export function isOmittedDistrictHeatingValue(
  parameter: Parameter | undefined
): boolean {
  if (parameter === undefined) {
    return true;
  }
  const v = parameter.value;
  if (v === null || v === undefined || v === "") {
    return true;
  }
  const raw = Number(v);
  return raw === 0;
}

export function buildSupplementalHeatingTooltipMirrors(
  parameters: Parameter[],
  heatingTechnologies: { id: string; title: string }[],
  labels: string[],
  suffixes: string[],
  colors: string[]
): Record<string, SupplementalHeatingTooltipBreakdown> | undefined {
  const out: Record<string, SupplementalHeatingTooltipBreakdown> = {};

  for (const tech of heatingTechnologies) {
    const rows: SupplementalHeatingTooltipBreakdown["rows"] = [];

    suffixes.forEach((suffix, rowIndex) => {
      const paramId = supplementalDistrictHeatingParameterId(tech.id, suffix);
      if (!paramId) {
        return;
      }

      const param = parameters.find((p) => p.id === paramId);
      if (isOmittedDistrictHeatingValue(param)) {
        return;
      }

      let label = labels[rowIndex] ?? suffix;
      label = escapeTooltipHtml(label);

      rows.push({
        color: colors[rowIndex] ?? "#666",
        label,
        value: param?.value,
      });
    });

    if (rows.length > 0) {
      out[tech.title] = { rows };
    }
  }

  return Object.keys(out).length ? out : undefined;
}
